import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFileConvert } from '../../hooks/useFileConvert';
import Layout from '../../components/feature/Layout';
import ConversionSuccess from '../../components/feature/ConversionSuccess';

type ConvertMode = 'pdf-to-image' | 'image-to-pdf';

export default function ImageConvertPage() {
  const { t } = useTranslation();
  const [mode, setMode] = useState<ConvertMode>('pdf-to-image');
  const [files, setFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);

  const {
    file, status, progress, error, downloadUrl, fileName,
    setFile, startConversion, download, reset
  } = useFileConvert();

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || window.location.origin;
    
    // Set page title
    document.title = 'Image Converter - PDF to Image & Image to PDF | FreePDFTools';
    
    // Set meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Convert PDF to images or images to PDF online for free. Support JPG, PNG, and multiple image formats. Fast, secure, and easy to use.');
    
    // Set canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', `${siteUrl}/image-convert`);
    
    // SoftwareApplication Schema
    const softwareSchema = {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "PDF Image Converter",
      "url": `${siteUrl}/image-convert`,
      "applicationCategory": "UtilitiesApplication",
      "operatingSystem": "Any",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD"
      },
      "description": "Convert PDF to images or images to PDF online for free. Support multiple image formats including JPG, PNG, and more.",
      "featureList": [
        "Convert PDF to high-quality images",
        "Merge multiple images into one PDF",
        "Support JPG, PNG, GIF, BMP, WebP formats",
        "Batch image processing",
        "Maintain image quality and resolution"
      ],
      "screenshot": `${siteUrl}/og-image.png`,
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.7",
        "ratingCount": "11680",
        "bestRating": "5",
        "worstRating": "1"
      }
    };

    // HowTo Schema
    const howToSchema = {
      "@context": "https://schema.org",
      "@type": "HowTo",
      "name": "How to Convert PDF to Image or Image to PDF",
      "description": "Step-by-step guide to convert between PDF and image formats online",
      "step": [
        {
          "@type": "HowToStep",
          "position": 1,
          "name": "Select Conversion Mode",
          "text": "Choose whether you want to convert PDF to Image or Image to PDF",
          "url": `${siteUrl}/image-convert#step1`
        },
        {
          "@type": "HowToStep",
          "position": 2,
          "name": "Upload Files",
          "text": "Click 'Select File' or drag and drop your PDF or image files into the upload area",
          "url": `${siteUrl}/image-convert#step2`
        },
        {
          "@type": "HowToStep",
          "position": 3,
          "name": "Download Converted File",
          "text": "Once conversion is complete, download your converted file",
          "url": `${siteUrl}/image-convert#step3`
        }
      ],
      "totalTime": "PT2M"
    };

    const softwareScript = document.createElement('script');
    softwareScript.type = 'application/ld+json';
    softwareScript.text = JSON.stringify(softwareSchema);
    document.head.appendChild(softwareScript);

    const howToScript = document.createElement('script');
    howToScript.type = 'application/ld+json';
    howToScript.text = JSON.stringify(howToSchema);
    document.head.appendChild(howToScript);

    return () => {
      document.head.removeChild(softwareScript);
      document.head.removeChild(howToScript);
    };
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      if (mode === 'image-to-pdf' && e.target.files.length > 1) {
        const fileArray = Array.from(e.target.files);
        setFiles(fileArray);
        setFile(fileArray[0]);
        startConversion('image-to-pdf', ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp']);
      } else if (e.target.files[0]) {
        const selectedFile = e.target.files[0];
        setFile(selectedFile);
        const allowedFormats = mode === 'pdf-to-image' ? ['pdf'] : ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];
        startConversion(mode, allowedFormats);
      }
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files) {
      if (mode === 'image-to-pdf' && e.dataTransfer.files.length > 1) {
        const fileArray = Array.from(e.dataTransfer.files);
        setFiles(fileArray);
        setFile(fileArray[0]);
        startConversion('image-to-pdf', ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp']);
      } else if (e.dataTransfer.files[0]) {
        const selectedFile = e.dataTransfer.files[0];
        setFile(selectedFile);
        const allowedFormats = mode === 'pdf-to-image' ? ['pdf'] : ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];
        startConversion(mode, allowedFormats);
      }
    }
  };

  const handleModeChange = (newMode: ConvertMode) => {
    setMode(newMode);
    reset();
    setFiles([]);
  };

  const handleReset = () => { reset(); setFiles([]); };

  const isProcessing = status === 'uploading' || status === 'converting' || status === 'downloading';
  const acceptFormats = mode === 'pdf-to-image' ? '.pdf' : 'image/*';
  const multipleFiles = mode === 'image-to-pdf';

  return (
    <Layout>
      <div className="bg-white min-h-screen">
        <div className="w-full bg-gray-50 border-b border-dashed border-gray-300 py-3">
          <div className="max-w-4xl mx-auto text-center text-gray-400 text-sm">{t('ads.topBanner')} 728×90</div>
        </div>

        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="text-center mb-10">
            <div className="w-16 h-16 bg-pink-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <i className="ri-image-line text-3xl text-pink-600"></i>
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">{t('imageConvert.title')}</h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">{t('imageConvert.description')}</p>
          </div>

          {/* Mode Switcher */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex bg-gray-100 rounded-full p-1">
              <button
                onClick={() => handleModeChange('pdf-to-image')}
                className={`px-6 py-2 rounded-full font-medium transition-all whitespace-nowrap ${mode === 'pdf-to-image' ? 'bg-pink-600 text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
              >
                {t('imageConvert.pdfToImage')}
              </button>
              <button
                onClick={() => handleModeChange('image-to-pdf')}
                className={`px-6 py-2 rounded-full font-medium transition-all whitespace-nowrap ${mode === 'image-to-pdf' ? 'bg-pink-600 text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
              >
                {t('imageConvert.imageToPdf')}
              </button>
            </div>
          </div>

          <div className="mb-10">
            {!file && !isProcessing && status !== 'completed' && status !== 'error' && (
              <div
                className={`border-2 border-dashed rounded-2xl p-14 text-center transition-all cursor-pointer ${
                  isDragging ? 'border-pink-500 bg-pink-50' : 'border-gray-300 bg-gray-50 hover:border-pink-400 hover:bg-pink-50/40'
                }`}
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
              >
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-5 shadow-sm">
                  <i className="ri-upload-cloud-2-line text-4xl text-pink-600"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('upload.dragDrop')}</h3>
                <p className="text-gray-500 mb-6">{t('upload.or')}</p>
                <label className="inline-block cursor-pointer">
                  <input type="file" accept={acceptFormats} multiple={multipleFiles} onChange={handleFileSelect} className="hidden" />
                  <span className="px-8 py-3 bg-pink-600 text-white rounded-lg font-medium hover:bg-pink-700 transition-colors inline-block whitespace-nowrap">
                    {multipleFiles ? t('imageConvert.selectImages') : t('upload.selectFile')}
                  </span>
                </label>
                <p className="text-sm text-gray-400 mt-5">
                  {multipleFiles ? t('imageConvert.multipleFilesHint') : t('upload.fileSize')}
                </p>
              </div>
            )}

            {isProcessing && (
              <div className="border-2 border-pink-200 rounded-2xl p-12 text-center bg-pink-50">
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-5">
                  <i className="ri-loader-4-line text-4xl text-pink-600 animate-spin"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {status === 'uploading' && t('upload.uploading')}
                  {status === 'converting' && t('upload.converting')}
                  {status === 'downloading' && t('upload.preparing')}
                </h3>
                <p className="text-gray-600 mb-4">{files.length > 1 ? t('imageConvert.processingFiles', { count: files.length }) : file?.name}</p>
                <div className="max-w-md mx-auto">
                  <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                    <div className="bg-pink-600 h-3 rounded-full transition-all duration-300" style={{ width: `${progress}%` }}></div>
                  </div>
                  <p className="text-sm text-gray-500">{progress}%</p>
                </div>
              </div>
            )}

            {status === 'completed' && downloadUrl && (
              <ConversionSuccess
                fileName={fileName}
                originalName={files.length > 1 ? `${files.length} 个文件` : file?.name}
                accentColor="pink"
                onDownload={download}
                onReset={handleReset}
              />
            )}

            {status === 'error' && error && (
              <div className="border-2 border-red-200 rounded-2xl p-12 text-center bg-red-50">
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-5">
                  <i className="ri-error-warning-line text-4xl text-red-600"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('upload.error')}</h3>
                <p className="text-red-600 mb-6">{error.message}</p>
                <button onClick={handleReset} className="px-8 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors whitespace-nowrap">{t('upload.retry')}</button>
              </div>
            )}
          </div>

          <div className="w-full bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl py-8 mb-10 text-center text-gray-400 text-sm">
            {t('ads.middleBanner')} 728×90
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-10">
            {[
              { icon: 'ri-file-list-3-line', key: 'feature1' },
              { icon: 'ri-stack-line', key: 'feature2' },
              { icon: 'ri-hd-line', key: 'feature3' },
            ].map((f) => (
              <div key={f.key} className="text-center p-6 bg-gray-50 rounded-xl">
                <div className="w-12 h-12 bg-pink-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <i className={`${f.icon} text-xl text-pink-600`}></i>
                </div>
                <h3 className="font-semibold text-gray-900">{t(`imageConvert.${f.key}`)}</h3>
              </div>
            ))}
          </div>

          <div className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">{t('upload.howItWorks')}</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {[1, 2, 3].map((n) => (
                <div key={n} className="text-center">
                  <div className="w-12 h-12 bg-pink-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold text-lg">{n}</div>
                  <h3 className="font-semibold text-gray-900 mb-2">{t(`upload.step${n}`)}</h3>
                  <p className="text-sm text-gray-600">{t(`upload.step${n}Desc`)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
